import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, Upload, ArrowLeft, ListChecks } from "lucide-react";

export default function AdminTests() {
  const [tests, setTests] = useState([]);
  const [products, setProducts] = useState([]);
  const [exams, setExams] = useState([]);
  const [view, setView] = useState({ mode: "list" }); // list | questions
  const [openTest, setOpenTest] = useState(false);
  const [tform, setTform] = useState({});
  const [editingTest, setEditingTest] = useState(null);

  const loadTests = useCallback(() => api.get("/admin/tests").then((r) => setTests(r.data)).catch(() => {}), []);
  useEffect(() => {
    loadTests();
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/exams").then((r) => setExams(r.data)).catch(() => {});
  }, [loadTests]);

  const productName = (id) => products.find((p) => p.id === id)?.name || "—";

  const openNewTest = () => { setEditingTest(null); setTform({ published: false, is_free: false, negative_marking: true, duration_minutes: 30, total_marks: 0 }); setOpenTest(true); };
  const openEditTest = (t) => { setEditingTest(t); setTform({ ...t }); setOpenTest(true); };
  const saveTest = async () => {
    try {
      if (editingTest) await api.put(`/admin/tests/${editingTest.id}`, tform);
      else await api.post("/admin/tests", tform);
      toast.success("Saved"); setOpenTest(false); loadTests();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delTest = async (id) => { if (!window.confirm("Delete this test?")) return; await api.delete(`/admin/tests/${id}`); toast.success("Deleted"); loadTests(); };

  const togglePublish = async (t) => {
    const next = !t.published;
    try {
      await api.put(`/admin/tests/${t.id}`, { published: next });
      toast.success(next ? "Test published" : "Test unpublished");
      if (next) {
        const prod = products.find((p) => p.id === t.product_id);
        if (prod && !prod.published) toast.warning(`Series "${prod.name}" is still unpublished — students won't see this test until you publish the series (and its exam).`);
      }
      loadTests();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleFree = async (t) => {
    const next = !t.is_free;
    try {
      await api.put(`/admin/tests/${t.id}`, { is_free: next });
      toast.success(next ? "Marked as FREE — anyone can attempt" : "Marked as PAID — requires purchase");
      loadTests();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (view.mode === "questions") {
    return <QuestionManager test={view.test} exams={exams} onBack={() => { setView({ mode: "list" }); loadTests(); }} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-2xl font-bold">Tests & Questions</h1><p className="text-sm text-content-secondary mt-1">Create tests, add or import questions.</p></div>
        <Button className="btn-ember rounded-full" onClick={openNewTest} data-testid="admin-tests-add"><Plus className="w-4 h-4 mr-1" /> New Test</Button>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left text-content-muted">
            <th className="px-4 py-3 font-medium">Test</th><th className="px-4 py-3 font-medium">Series</th>
            <th className="px-4 py-3 font-medium">Qs</th><th className="px-4 py-3 font-medium">Access</th>
            <th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3" />
          </tr></thead>
          <tbody>
            {tests.map((t) => (
              <tr key={t.id} className="border-b border-line/50 hover:bg-card-hover/50" data-testid={`admin-test-row-${t.id}`}>
                <td className="px-4 py-3 font-medium">{t.name || t.title}</td>
                <td className="px-4 py-3 text-content-secondary max-w-xs truncate">{productName(t.product_id)}</td>
                <td className="px-4 py-3">{t.total_questions}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={!!t.is_free} onCheckedChange={() => toggleFree(t)} data-testid={`admin-test-free-toggle-${t.id}`} aria-label="Toggle free/paid" />
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${t.is_free ? "bg-sky-500/15 text-sky-400" : "bg-card-hover text-content-muted"}`}>{t.is_free ? "Free" : "Paid"}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={!!t.published} onCheckedChange={() => togglePublish(t)} data-testid={`admin-test-publish-toggle-${t.id}`} aria-label="Toggle published" />
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${t.published ? "bg-emerald-500/15 text-emerald-400" : "bg-card-hover text-content-muted"}`}>{t.published ? "Published" : "Draft"}</span>
                  </div>
                </td>
                <td className="px-4 py-3"><div className="flex gap-1 justify-end">
                  <button onClick={() => setView({ mode: "questions", test: t })} className="p-1.5 hover:text-ember" title="Questions" data-testid={`admin-test-questions-${t.id}`}><ListChecks className="w-4 h-4" /></button>
                  <button onClick={() => openEditTest(t)} className="p-1.5 hover:text-ember"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => delTest(t.id)} className="p-1.5 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div></td>
              </tr>
            ))}
            {tests.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-content-muted">No tests yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={openTest} onOpenChange={setOpenTest}>
        <DialogContent className="bg-card border-line max-w-xl">
          <DialogHeader><DialogTitle>{editingTest ? "Edit" : "New"} Test</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className="text-sm text-content-secondary">Test name</label><Input value={tform.name || ""} onChange={(e) => setTform({ ...tform, name: e.target.value })} className="mt-1 bg-ink border-line" data-testid="test-form-name" /></div>
            <div className="sm:col-span-2"><label className="text-sm text-content-secondary">Test series</label>
              <select value={tform.product_id || ""} onChange={(e) => setTform({ ...tform, product_id: e.target.value })} className="mt-1 w-full h-10 rounded-md bg-ink border border-line px-3 text-sm" data-testid="test-form-product">
                <option value="">— Select series —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div><label className="text-sm text-content-secondary">Duration (min)</label><Input type="number" value={tform.duration_minutes ?? 30} onChange={(e) => setTform({ ...tform, duration_minutes: Number(e.target.value) })} className="mt-1 bg-ink border-line" /></div>
            <div><label className="text-sm text-content-secondary">Total marks</label><Input type="number" value={tform.total_marks ?? 0} onChange={(e) => setTform({ ...tform, total_marks: Number(e.target.value) })} className="mt-1 bg-ink border-line" /></div>
            <div className="sm:col-span-2"><label className="text-sm text-content-secondary">Instructions</label><Textarea value={tform.instructions || ""} onChange={(e) => setTform({ ...tform, instructions: e.target.value })} className="mt-1 bg-ink border-line" /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!tform.is_free} onCheckedChange={(v) => setTform({ ...tform, is_free: v })} data-testid="test-form-free" /> Free demo test</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!tform.published} onCheckedChange={(v) => setTform({ ...tform, published: v })} data-testid="test-form-published" /> Published</label>
          </div>
          <DialogFooter><Button variant="outline" className="border-line" onClick={() => setOpenTest(false)}>Cancel</Button><Button className="btn-ember" onClick={saveTest} data-testid="test-form-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuestionManager({ test, exams, onBack }) {
  const [questions, setQuestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get(`/admin/tests/${test.id}/questions`).then((r) => setQuestions(r.data)).catch(() => {}), [test.id]);
  useEffect(() => { load(); }, [load]);

  const blank = () => ({ question_text: "", options: [{ id: "a", text: "" }, { id: "b", text: "" }, { id: "c", text: "" }, { id: "d", text: "" }], correct_option_ids: ["a"], explanation: "", subject: "General", difficulty: "medium", marks: 2, negative_marks: 0.5, test_id: test.id, exam_id: test.exam_id });
  const openNew = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (q) => { setEditing(q); setForm({ ...q }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/admin/questions/${editing.id}`, form);
      else await api.post("/admin/questions", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete question?")) return; await api.delete(`/admin/questions/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <button onClick={onBack} className="text-sm text-content-secondary flex items-center gap-1 hover:text-ember mb-4"><ArrowLeft className="w-4 h-4" /> Back to tests</button>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div><h1 className="font-heading text-xl font-bold">{test.name}</h1><p className="text-sm text-content-secondary">{questions.length} questions</p></div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-line rounded-full" onClick={() => setImportOpen(true)} data-testid="admin-import-btn"><Upload className="w-4 h-4 mr-1" /> Import</Button>
          <Button className="btn-ember rounded-full" onClick={openNew} data-testid="admin-add-question-btn"><Plus className="w-4 h-4 mr-1" /> Add Question</Button>
        </div>
      </div>
      <div className="space-y-3">
        {questions.map((q, i) => (
          <div key={q.id} className="card-surface p-4">
            <div className="flex justify-between gap-3">
              <div className="text-sm"><span className="text-content-muted mr-2 font-mono">Q{i + 1}.</span>{q.question_text}</div>
              <div className="flex gap-1 shrink-0"><button onClick={() => openEdit(q)} className="p-1 hover:text-ember"><Pencil className="w-4 h-4" /></button><button onClick={() => del(q.id)} className="p-1 hover:text-red-400"><Trash2 className="w-4 h-4" /></button></div>
            </div>
            <div className="text-xs text-content-muted mt-1">Correct: {(q.correct_option_ids || []).join(", ").toUpperCase()} • {q.subject} • {q.difficulty}</div>
          </div>
        ))}
        {questions.length === 0 && <div className="card-surface p-10 text-center text-content-muted">No questions. Add or import them.</div>}
      </div>

      {open && form && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-card border-line max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Question</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><label className="text-sm text-content-secondary">Question text</label><Textarea value={form.question_text} onChange={(e) => setForm({ ...form, question_text: e.target.value })} className="mt-1 bg-ink border-line" data-testid="q-text" /></div>
              {form.options.map((o, i) => (
                <div key={o.id} className="flex items-center gap-2">
                  <input type="checkbox" checked={form.correct_option_ids.includes(o.id)} onChange={(e) => setForm({ ...form, correct_option_ids: e.target.checked ? [...form.correct_option_ids, o.id] : form.correct_option_ids.filter((x) => x !== o.id) })} className="w-4 h-4 accent-[#F97316]" data-testid={`q-correct-${o.id}`} />
                  <span className="font-mono text-xs w-5 uppercase">{o.id}</span>
                  <Input value={o.text} onChange={(e) => { const opts = [...form.options]; opts[i] = { ...o, text: e.target.value }; setForm({ ...form, options: opts }); }} className="bg-ink border-line" placeholder={`Option ${o.id.toUpperCase()}`} data-testid={`q-opt-${o.id}`} />
                </div>
              ))}
              <div><label className="text-sm text-content-secondary">Explanation</label><Textarea value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} className="mt-1 bg-ink border-line" /></div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><label className="text-xs text-content-muted">Subject</label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1 bg-ink border-line" /></div>
                <div><label className="text-xs text-content-muted">Difficulty</label><Input value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="mt-1 bg-ink border-line" /></div>
                <div><label className="text-xs text-content-muted">Marks</label><Input type="number" value={form.marks} onChange={(e) => setForm({ ...form, marks: Number(e.target.value) })} className="mt-1 bg-ink border-line" /></div>
                <div><label className="text-xs text-content-muted">Negative</label><Input type="number" value={form.negative_marks} onChange={(e) => setForm({ ...form, negative_marks: Number(e.target.value) })} className="mt-1 bg-ink border-line" /></div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" className="border-line" onClick={() => setOpen(false)}>Cancel</Button><Button className="btn-ember" onClick={save} data-testid="q-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {importOpen && <ImportDialog test={test} onClose={() => { setImportOpen(false); load(); }} />}
    </div>
  );
}

function ImportDialog({ test, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const sample = `question,option_a,option_b,option_c,option_d,correct_answer,explanation,subject,difficulty,marks,negative_marks
"2+2=?",3,4,5,6,b,"Basic addition",Maths,easy,2,0.5`;

  const doImport = async () => {
    setBusy(true);
    try {
      let rows = [];
      const trimmed = text.trim();
      if (trimmed.startsWith("[")) rows = JSON.parse(trimmed);
      else {
        const lines = trimmed.split(/\r?\n/).filter(Boolean);
        const headers = parseCsvLine(lines[0]);
        rows = lines.slice(1).map((ln) => { const vals = parseCsvLine(ln); const o = {}; headers.forEach((h, i) => o[h.trim()] = vals[i]); return o; });
      }
      const { data } = await api.post("/admin/questions/import", { test_id: test.id, exam_id: test.exam_id, rows });
      toast.success(`Imported ${data.imported} questions${data.errors.length ? `, ${data.errors.length} errors` : ""}`);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invalid format"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-line max-w-2xl">
        <DialogHeader><DialogTitle>Import Questions (CSV or JSON)</DialogTitle></DialogHeader>
        <p className="text-xs text-content-muted">Columns: question, option_a-d, correct_answer (a/b/c/d), explanation, subject, chapter, difficulty, marks, negative_marks, year</p>
        <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder={sample} className="bg-ink border-line font-mono text-xs" data-testid="import-textarea" />
        <button className="text-xs text-ember text-left" onClick={() => setText(sample)}>Load sample CSV</button>
        <DialogFooter><Button variant="outline" className="border-line" onClick={onClose}>Cancel</Button><Button className="btn-ember" disabled={busy || !text.trim()} onClick={doImport} data-testid="import-submit">{busy ? "Importing…" : "Import"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function parseCsvLine(line) {
  const out = []; let cur = ""; let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
    else if (c === "," && !inQ) { out.push(cur); cur = ""; }
    else cur += c;
  }
  out.push(cur);
  return out;
}
